/**
 * Attendance Module - Shared Data & Utilities
 * Contains dummy data, constants, and helper functions
 */

// Shift configurations
export const SHIFTS = [
  { id: 1, name: 'Morning Shift', code: 'MS', startTime: '06:00', endTime: '14:00', color: 'amber' },
  { id: 2, name: 'Day Shift', code: 'DS', startTime: '09:00', endTime: '18:00', color: 'blue' },
  { id: 3, name: 'Evening Shift', code: 'ES', startTime: '14:00', endTime: '22:00', color: 'orange' },
  { id: 4, name: 'Night Shift', code: 'NS', startTime: '22:00', endTime: '06:00', color: 'indigo' },
];

// Attendance status configurations
export const ATTENDANCE_STATUS = {
  present: { label: 'Present', bg: 'bg-emerald-100', text: 'text-emerald-700', border: 'border-emerald-200' },
  absent: { label: 'Absent', bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-200' },
  late: { label: 'Late', bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-200' },
  halfday: { label: 'Half Day', bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-200' },
  leave: { label: 'On Leave', bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-200' },
  holiday: { label: 'Holiday', bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-200' },
  weekend: { label: 'Weekend', bg: 'bg-gray-100', text: 'text-gray-700', border: 'border-gray-200' },
};

// Check-in method configurations
export const CHECK_IN_METHODS = {
  biometric: { label: 'Biometric', icon: 'fingerprint' },
  mobile: { label: 'Mobile App', icon: 'smartphone' },
  web: { label: 'Web Portal', icon: 'monitor' },
  manual: { label: 'Manual Entry', icon: 'edit' },
  kiosk: { label: 'Kiosk', icon: 'tablet' },
};

// Regularization types
export const REGULARIZATION_TYPES = [
  { id: 'missed_checkin', label: 'Missed Check-In', description: 'Forgot to check in at shift start' },
  { id: 'missed_checkout', label: 'Missed Check-Out', description: 'Forgot to check out at shift end' },
  { id: 'late_arrival', label: 'Late Arrival', description: 'Request to mark as on-time' },
  { id: 'early_departure', label: 'Early Departure', description: 'Left early with permission' },
  { id: 'on_duty', label: 'On Duty', description: 'Working from field/client location' },
  { id: 'work_from_home', label: 'Work From Home', description: 'Remote work day' },
];

// Departments
export const DEPARTMENTS = [
  { id: 1, name: 'Kitchen' },
  { id: 2, name: 'Service' },
  { id: 3, name: 'Admin' },
  { id: 4, name: 'Housekeeping' },
  { id: 5, name: 'Security' },
];

// Generate dummy employees
export const generateEmployees = () => [
  { id: 1, empCode: 'EMP001', name: 'Rahul Sharma', department: 'Kitchen', designation: 'Head Chef', shift: 'MS', phone: '9876543210', email: 'rahul@resto.com' },
  { id: 2, empCode: 'EMP002', name: 'Priya Patel', department: 'Service', designation: 'Floor Manager', shift: 'DS', phone: '9876543211', email: 'priya@resto.com' },
  { id: 3, empCode: 'EMP003', name: 'Amit Kumar', department: 'Kitchen', designation: 'Sous Chef', shift: 'ES', phone: '9876543212', email: 'amit@resto.com' },
  { id: 4, empCode: 'EMP004', name: 'Sneha Reddy', department: 'Service', designation: 'Senior Waiter', shift: 'DS', phone: '9876543213', email: 'sneha@resto.com' },
  { id: 5, empCode: 'EMP005', name: 'Vikram Singh', department: 'Kitchen', designation: 'Line Cook', shift: 'MS', phone: '9876543214', email: 'vikram@resto.com' },
  { id: 6, empCode: 'EMP006', name: 'Anita Verma', department: 'Admin', designation: 'Accountant', shift: 'DS', phone: '9876543215', email: 'anita@resto.com' },
  { id: 7, empCode: 'EMP007', name: 'Kiran Joshi', department: 'Service', designation: 'Waiter', shift: 'ES', phone: '9876543216', email: 'kiran@resto.com' },
  { id: 8, empCode: 'EMP008', name: 'Deepak Nair', department: 'Kitchen', designation: 'Prep Cook', shift: 'MS', phone: '9876543217', email: 'deepak@resto.com' },
  { id: 9, empCode: 'EMP009', name: 'Meera Iyer', department: 'Service', designation: 'Hostess', shift: 'DS', phone: '9876543218', email: 'meera@resto.com' },
  { id: 10, empCode: 'EMP010', name: 'Rajesh Gupta', department: 'Kitchen', designation: 'Dishwasher', shift: 'ES', phone: '9876543219', email: 'rajesh@resto.com' },
  { id: 11, empCode: 'EMP011', name: 'Sunita Devi', department: 'Housekeeping', designation: 'Cleaner', shift: 'MS', phone: '9876543220', email: 'sunita@resto.com' },
  { id: 12, empCode: 'EMP012', name: 'Mohammad Ali', department: 'Security', designation: 'Guard', shift: 'NS', phone: '9876543221', email: 'ali@resto.com' },
];

// Generate attendance log for a date
export const generateAttendanceLog = (employeesParam, date) => {
  // Use passed employees or generate them
  const employees = employeesParam || generateEmployees();
  const statuses = ['present', 'present', 'present', 'present', 'present', 'late', 'absent', 'leave'];
  const methods = ['biometric', 'mobile', 'web', 'biometric', 'mobile'];
  const locations = ['Main Kitchen', 'Floor 1', 'Floor 2', 'Reception', 'Back Office', 'Prep Area'];

  return employees.map((emp, idx) => {
    const status = statuses[idx % statuses.length];
    const isPresent = status === 'present' || status === 'late';
    const shift = SHIFTS.find(s => s.code === emp.shift);

    // Generate random check-in time based on shift and status
    let checkIn = null;
    let checkOut = null;

    if (isPresent) {
      const shiftStart = parseInt(shift?.startTime?.split(':')[0] || 9);
      const lateMinutes = status === 'late' ? Math.floor(Math.random() * 45) + 15 : Math.floor(Math.random() * 10) - 5;
      const checkInHour = shiftStart + Math.floor(lateMinutes / 60);
      const checkInMin = ((lateMinutes % 60) + 60) % 60;
      checkIn = `${String(checkInHour).padStart(2, '0')}:${String(checkInMin).padStart(2, '0')}`;

      // Generate checkout (some employees may not have checked out yet)
      if (Math.random() > 0.3) {
        const shiftEnd = parseInt(shift?.endTime?.split(':')[0] || 18);
        const overtimeMinutes = Math.floor(Math.random() * 60);
        const checkOutHour = shiftEnd + Math.floor(overtimeMinutes / 60);
        const checkOutMin = overtimeMinutes % 60;
        checkOut = `${String(checkOutHour).padStart(2, '0')}:${String(checkOutMin).padStart(2, '0')}`;
      }
    }

    // Calculate hours worked
    let hoursWorked = null;
    if (checkIn && checkOut) {
      const [inH, inM] = checkIn.split(':').map(Number);
      const [outH, outM] = checkOut.split(':').map(Number);
      const totalMinutes = (outH * 60 + outM) - (inH * 60 + inM);
      const hours = Math.floor(totalMinutes / 60);
      const mins = totalMinutes % 60;
      hoursWorked = `${hours}h ${mins}m`;
    }

    return {
      id: idx + 1,
      date,
      employee: emp,
      empCode: emp.empCode,
      name: emp.name,
      department: emp.department,
      designation: emp.designation,
      shift: emp.shift,
      shiftTiming: shift ? `${shift.startTime} - ${shift.endTime}` : '',
      checkIn,
      checkOut,
      status,
      hoursWorked,
      method: isPresent ? methods[idx % methods.length] : null,
      location: isPresent ? locations[idx % locations.length] : null,
      overtimeHours: checkOut && Math.random() > 0.7 ? `${Math.floor(Math.random() * 2) + 1}h` : null,
      remarks: status === 'leave' ? 'Sick Leave' : status === 'absent' ? 'Unplanned' : null,
    };
  });
};

// Generate weekly data for chart
export const generateWeeklyData = () => {
  const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  return days.map(day => ({
    day,
    present: Math.floor(Math.random() * 5) + 18,
    absent: Math.floor(Math.random() * 4) + 1,
    late: Math.floor(Math.random() * 3),
    leave: Math.floor(Math.random() * 2),
  }));
};

// Generate regularization requests
export const generateRegularizationRequests = () => [
  { id: 1, empCode: 'EMP002', name: 'Priya Patel', department: 'Service', date: '2024-12-28', type: 'missed_checkout', reason: 'System was down, forgot to check out', requestedTime: '18:30', status: 'pending', createdAt: '2024-12-28T19:00:00', approver: null },
  { id: 2, empCode: 'EMP003', name: 'Amit Kumar', department: 'Kitchen', date: '2024-12-27', type: 'late_arrival', reason: 'Traffic jam due to accident', requestedTime: '09:00', actualTime: '09:32', status: 'pending', createdAt: '2024-12-27T10:00:00', approver: null },
  { id: 3, empCode: 'EMP005', name: 'Vikram Singh', department: 'Kitchen', date: '2024-12-26', type: 'on_duty', reason: 'Supplier visit for quality check', status: 'approved', createdAt: '2024-12-26T08:00:00', approver: 'Manager', approvedAt: '2024-12-26T09:00:00' },
  { id: 4, empCode: 'EMP007', name: 'Kiran Joshi', department: 'Service', date: '2024-12-25', type: 'missed_checkin', reason: 'Biometric machine not working', requestedTime: '14:00', status: 'rejected', createdAt: '2024-12-25T14:30:00', approver: 'Manager', rejectedAt: '2024-12-25T15:00:00', rejectionReason: 'No evidence provided' },
  { id: 5, empCode: 'EMP004', name: 'Sneha Reddy', department: 'Service', date: '2024-12-24', type: 'early_departure', reason: 'Doctor appointment', requestedTime: '16:00', status: 'approved', createdAt: '2024-12-24T15:30:00', approver: 'Manager', approvedAt: '2024-12-24T15:45:00' },
];

// Calculate today's stats
export const calculateTodayStats = (attendanceLog) => {
  const total = attendanceLog.length;
  const present = attendanceLog.filter(a => a.status === 'present').length;
  const absent = attendanceLog.filter(a => a.status === 'absent').length;
  const late = attendanceLog.filter(a => a.status === 'late').length;
  const onLeave = attendanceLog.filter(a => a.status === 'leave').length;
  const halfDay = attendanceLog.filter(a => a.status === 'halfday').length;

  // Calculate average check-in time
  const checkInTimes = attendanceLog
    .filter(a => a.checkIn)
    .map(a => {
      const [h, m] = a.checkIn.split(':').map(Number);
      return h * 60 + m;
    });
  const avgCheckInMins = checkInTimes.length > 0
    ? Math.round(checkInTimes.reduce((a, b) => a + b, 0) / checkInTimes.length)
    : 0;
  const avgCheckIn = `${String(Math.floor(avgCheckInMins / 60)).padStart(2, '0')}:${String(avgCheckInMins % 60).padStart(2, '0')}`;

  // Calculate average check-out time
  const checkOutTimes = attendanceLog
    .filter(a => a.checkOut)
    .map(a => {
      const [h, m] = a.checkOut.split(':').map(Number);
      return h * 60 + m;
    });
  const avgCheckOutMins = checkOutTimes.length > 0
    ? Math.round(checkOutTimes.reduce((a, b) => a + b, 0) / checkOutTimes.length)
    : 0;
  const avgCheckOut = `${String(Math.floor(avgCheckOutMins / 60)).padStart(2, '0')}:${String(avgCheckOutMins % 60).padStart(2, '0')}`;

  // Calculate average hours
  const hoursWorked = attendanceLog
    .filter(a => a.hoursWorked)
    .map(a => {
      const match = a.hoursWorked.match(/(\d+)h\s*(\d+)?m?/);
      if (match) {
        return parseInt(match[1]) * 60 + (parseInt(match[2]) || 0);
      }
      return 0;
    });
  const avgHoursMins = hoursWorked.length > 0
    ? Math.round(hoursWorked.reduce((a, b) => a + b, 0) / hoursWorked.length)
    : 0;
  const avgHours = `${Math.floor(avgHoursMins / 60)}h ${avgHoursMins % 60}m`;

  return {
    total,
    present: present + late, // Count late as present
    absent,
    late,
    onLeave,
    halfDay,
    avgCheckIn: avgCheckIn !== '00:00' ? formatTime(avgCheckIn) : '--:--',
    avgCheckOut: avgCheckOut !== '00:00' ? formatTime(avgCheckOut) : '--:--',
    avgHours: avgHoursMins > 0 ? avgHours : '--',
    attendanceRate: total > 0 ? Math.round(((present + late) / total) * 100) : 0,
  };
};

// Format time to 12-hour format
export const formatTime = (time24) => {
  if (!time24) return '--:--';
  const [hours, minutes] = time24.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const hours12 = hours % 12 || 12;
  return `${hours12}:${String(minutes).padStart(2, '0')} ${period}`;
};

// Format date
export const formatDate = (dateStr) => {
  const date = new Date(dateStr);
  const options = { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' };
  return date.toLocaleDateString('en-IN', options);
};

// Get date range for week
export const getWeekRange = (date) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  const monday = new Date(d.setDate(diff));
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);

  const formatShort = (d) => d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  return `${formatShort(monday)} - ${formatShort(sunday)}, ${sunday.getFullYear()}`;
};

// Export attendance data to CSV format
// Accepts (filename, headers, data) or (data, filename) for backward compatibility
export const exportToCSV = (filenameOrData, headersOrFilename, dataOrUndefined) => {
  let filename, headers, rows;

  // Check if called with new signature: (filename, headers, data)
  if (Array.isArray(headersOrFilename) && Array.isArray(dataOrUndefined)) {
    filename = filenameOrData;
    headers = headersOrFilename;
    rows = dataOrUndefined;
  } else {
    // Old signature: (data, filename)
    const data = filenameOrData;
    filename = headersOrFilename || 'attendance_export';
    headers = ['Emp Code', 'Name', 'Department', 'Shift', 'Check In', 'Check Out', 'Hours', 'Status', 'Method', 'Location'];
    rows = data.map(row => [
      row.empCode,
      row.name,
      row.department,
      row.shift,
      row.checkIn || '-',
      row.checkOut || '-',
      row.hoursWorked || '-',
      row.status,
      row.method || '-',
      row.location || '-',
    ]);
  }

  const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
  const blob = new Blob([csvContent], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename.endsWith('.csv') ? filename : `${filename}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

export default {
  SHIFTS,
  ATTENDANCE_STATUS,
  CHECK_IN_METHODS,
  REGULARIZATION_TYPES,
  DEPARTMENTS,
  generateEmployees,
  generateAttendanceLog,
  generateWeeklyData,
  generateRegularizationRequests,
  calculateTodayStats,
  formatTime,
  formatDate,
  getWeekRange,
  exportToCSV,
};
